//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  TikTokMusicGenre,
  type TikTokMusicGenre as TikTokMusicGenreType,
} from '@apis/tiktok-trends/tiktokMusicGenre';
import {
  TikTokPopularCommercialTrack,
  TikTokPopularCommercialTrackClip,
} from '@apis/tiktok-trends/tiktokTrendsModel';
import { TikTokMusicSoundInfoConfig } from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import { TikTokPostDetailsFormTooltips } from '@universal-scheduling/components/scheduler/post-details-forms/tiktok/TikTokPostDetailsConstants';
import { isDefined } from '@utils/filterPredicates';
import { getMsToSeconds, getSecondsToMs } from '@utils/timingUtils';

export const musicSoundInfoMsToSeconds = (
  info: TikTokMusicSoundInfoConfig | undefined,
): TikTokMusicSoundInfoConfig | undefined => {
  if (!isDefined(info)) {
    return undefined;
  }
  return {
    ...info,
    start: isDefined(info.start) ? getMsToSeconds(info.start) : undefined,
    end: isDefined(info.end) ? getMsToSeconds(info.end) : undefined,
  };
};

export const musicSoundInfoSecondsToMs = (
  info: TikTokMusicSoundInfoConfig | undefined,
): TikTokMusicSoundInfoConfig | undefined => {
  if (!isDefined(info)) {
    return undefined;
  }
  return {
    ...info,
    start: isDefined(info.start) ? getSecondsToMs(info.start) : undefined,
    end: isDefined(info.end) ? getSecondsToMs(info.end) : undefined,
  };
};

/**
 * Converts a TikTok music genre enum value into a human-readable label.
 * Replaces underscores with spaces and normalizes each word to title case
 * by uppercasing the first letter after a non-letter boundary and
 * lowercasing subsequent letters.
 */
export const getTikTokGenreLabel = (genre: TikTokMusicGenreType): string => {
  if (genre === TikTokMusicGenre.All) {
    return 'All Genres';
  }

  return genre
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

export const resolveTrackInfo = (
  track: TikTokPopularCommercialTrack,
): TikTokPopularCommercialTrackClip => {
  // Select full duration clip if available, otherwise fallback to trending clip.
  const resolved = [track.fullDurationSongClip, track.trendingSongClip]
    .filter(isDefined)
    .filter(
      (clip) =>
        isDefined(clip.duration) &&
        isDefined(clip.songClipId) &&
        isDefined(clip.previewUrl),
    )
    .map((clip) => ({
      duration: clip.duration,
      songClipId: clip.songClipId,
      previewUrl: clip.previewUrl,
    }));

  return (
    resolved[0] ?? {
      duration: undefined,
      songClipId: undefined,
      previewUrl: undefined,
    }
  );
};

export const clampTrimStart = (
  rawStart: number,
  currentEnd: number,
  trackDuration: number,
  videoDurationSeconds: number | undefined,
): { end: number; start: number } => {
  const start = Math.max(0, Math.min(rawStart, trackDuration - 1));
  const maxEnd = isDefined(videoDurationSeconds)
    ? Math.max(start + 1, Math.min(trackDuration, start + videoDurationSeconds))
    : trackDuration;
  const end = Math.min(maxEnd, Math.max(currentEnd, start + 1));
  return { end, start };
};

export const clampTrimEnd = (
  rawEnd: number,
  currentStart: number,
  trackDuration: number,
  videoDurationSeconds: number | undefined,
): { end: number; start: number } => {
  const end = Math.max(1, Math.min(rawEnd, trackDuration));
  const effectiveVideoDurationSeconds = isDefined(videoDurationSeconds)
    ? Math.max(1, videoDurationSeconds)
    : undefined;
  const minStart = isDefined(effectiveVideoDurationSeconds)
    ? Math.max(0, end - effectiveVideoDurationSeconds)
    : 0;
  const start = Math.max(minStart, Math.min(currentStart, end - 1));
  return { end, start };
};

export const clampEndThumbMove = (
  rawEnd: number,
  anchorStart: number,
  trackDuration: number,
  videoDurationSeconds: number | undefined,
): [number, number] => {
  const effectiveVideoDuration = isDefined(videoDurationSeconds)
    ? Math.max(1, videoDurationSeconds)
    : undefined;
  const maxEnd = isDefined(effectiveVideoDuration)
    ? Math.min(trackDuration, anchorStart + effectiveVideoDuration)
    : trackDuration;
  return [anchorStart, Math.min(Math.max(rawEnd, anchorStart + 1), maxEnd)];
};

export const clampStartThumbMove = (
  newStart: number,
  anchorEnd: number,
  videoDurationSeconds: number | undefined,
): [number, number] => {
  const minStart = isDefined(videoDurationSeconds)
    ? Math.max(0, anchorEnd - videoDurationSeconds)
    : 0;
  return [Math.min(Math.max(newStart, minStart), anchorEnd - 1), anchorEnd];
};

/**
 * Formats a duration in seconds to MM:SS or MM:SS.t, showing tenths of a second
 * when the value is not a whole number. Used for TikTok music trim inputs.
 */
export const readableTrimDurationFromSeconds = (
  durationSeconds: number,
): string => {
  const roundedTenths = Math.round(durationSeconds * 10);
  const totalWholeSeconds = Math.floor(roundedTenths / 10);
  const tenths = roundedTenths % 10;
  const hours = Math.floor(totalWholeSeconds / 3600);
  const minutes = Math.floor((totalWholeSeconds % 3600) / 60);
  const seconds = totalWholeSeconds % 60;

  const displayHours = hours > 0 ? `${String(hours).padStart(2, '0')}:` : '';
  const displayMinutes = String(minutes).padStart(2, '0');
  const displaySeconds = String(seconds).padStart(2, '0');
  const displayTenths = tenths > 0 ? `.${tenths}` : '';

  return `${displayHours}${displayMinutes}:${displaySeconds}${displayTenths}`;
};

/**
 * Parses a time string in "M:SS" or "M:SS.t" format (e.g. "1:30" → 90, "1:30.5" → 90.5)
 * or plain seconds (e.g. "90" → 90, "1.5" → 1.5). Tenths are the finest supported
 * granularity. Returns undefined on invalid input.
 */
export const parseTimeInput = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (/^\d+:\d{1,2}(\.\d)?$/.test(trimmed)) {
    const colonIndex = trimmed.indexOf(':');
    const minutes = parseInt(trimmed.slice(0, colonIndex), 10);
    const seconds = parseFloat(trimmed.slice(colonIndex + 1));
    return seconds < 60 ? minutes * 60 + seconds : undefined;
  }
  if (/^\d+(\.\d)?$/.test(trimmed)) {
    return parseFloat(trimmed);
  }
  return undefined;
};

export const getAddMusicTooltip = (
  isVideoPost: boolean,
  hasMedia: boolean,
): string | undefined => {
  if (!isVideoPost) {
    return TikTokPostDetailsFormTooltips.UnavailableForPhotoPost;
  }
  if (!hasMedia) {
    return TikTokPostDetailsFormTooltips.CommercialMusicNoMedia;
  }
  return undefined;
};
